import { type SiteId, type VMSCameraId } from '@solinkcorp/api-types';
import {
  EVENT_TYPE_BOOKMARK,
  EVENT_TYPE_MOTION,
  EVENT_TYPE_TRANSACTION,
  type SearchOperator,
} from '@solinkcorp/vd-common';
import PlatformApi, { type EventQueryParameters, type IEvent } from 'clients/PlatformApi';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import moment from 'moment';
import type IntervalTree from 'node-interval-tree';
import {
  type BasicEventQueryParameters,
  checkEventAgainstSubtypeFilter,
  type Filter,
  getQueryParameters,
  isDefaultEventFilters,
} from 'utils/EventsFetchUtil';
import * as EventUtil from 'utils/EventUtil';
import { type EventFilterState } from 'utils/types';
import WOSCache from 'utils/WOSCache';
import { MASTER_TIME_RESOLUTION } from 'vd-constants';
import {
  checkEqMustHaveFields,
  checkEqMustNonZeroFields,
  convertEqMustHaveFields,
} from './EventCacheUtils';

const HOUR_IN_MS = 60 * 60 * 1000;

// Each event is about 5K in size (at least serialized as JSON).
// 200MB gives us cache for about 40K events, which is about 30 days of events
// for a single location at McCluskey.  Note that we could persist these to
// disk as well; if we want push these to IndexedDB, the minimum quota we are
// guaranteed is 10MB (although in practice we can assume it will be around 2GB
// on Firefox, and much larger on Chrome).
const APPROX_EVENT_SIZE = 5000;
const APPROX_MAX_CACHE_SIZE_IN_BYTES = 200000000;
const MAX_CACHED_EVENTS = APPROX_MAX_CACHE_SIZE_IN_BYTES / APPROX_EVENT_SIZE;

// This is used as a quick check to see if a query is part of "today".
// 25 hours because daylight saving time.  If an entry in the cache is from
// today, we'll check for new events every 10 minutes.
const MAX_TODAY_AGE = 25 * HOUR_IN_MS;
export const REVALIDATE_TODAY_PERIOD = 10 * 60 * 1000; // 10 minute revalidation for events from today.

export type EventCacheInterval =
  | { start: number; end: number }
  | { overlapTime: number; overlap?: number };

export function isOverlapInterval(
  time: EventCacheInterval,
): time is { overlapTime: number; overlap?: number } {
  return 'overlapTime' in time;
}

// Rules for revalidating data in the cache.  These need to be in sorted order by "age".
// If a cache entry is younger than "age", it will be revalidated from the server
// after `revalidateAfter` milliseconds.
const REVALIDATION_RULES = [
  // Revalidate entries from today every 10 mins
  {
    name: 'today',
    age: MAX_TODAY_AGE,
    revalidateAfter: REVALIDATE_TODAY_PERIOD,
  },
  // Revalidate entries from the past week every hour.
  { name: 'week', age: 7 * 24 * HOUR_IN_MS, revalidateAfter: HOUR_IN_MS },
];

/** The maximum number of events to try to fetch from Platform API at a time.
 * If we fetch too many, platform might run out of RAM.
 */
const MAX_EVENTS_SINGLE_FETCH = 1000;

/**
 * When fetching events from the cache, events are returned based on their
 * start time.  When fetching overlapping events, we need to "go back" a
 * certain amount to ensure we get any events that started before the overlap.
 * Generally we go back to the start of the day, however if we're right at the
 * start of the day, we don't want to miss events from the end of the previous day.
 * So, if we're within `MAX_EVENT_LENGTH_FOR_OVERLAP` of midnight, we'll include
 * the previous day's events in the query.
 *
 * In practical terms, this means if you're MAX_EVENT_LENGTH_FOR_OVERLAP+1 minutes
 * into a Monday, and there was a three hour event at the end of Sunday, it's
 * not going to show up.
 */
const MAX_EVENT_LENGTH_FOR_OVERLAP = 60 * 60 * 1000; // 1 Hour.

/**
 * A query sent to the EventCache.
 */
export interface EventCacheQuery {
  /**
   * If this is a `{start, end}`, only return events where `startTime >= start`
   * and `startTime < end`.
   *
   * If `{overlapTime, overlap}`, return events that overlap the specified
   * interval.  (Events that started more than 2 hours before the interval may
   * not be returned.)  This is extremely efficient for short intervals (e.g.
   * find all the events that are happening right now) but for longer intervals
   * (e.g. find all events for the day) it's better to just use `start` and `end`.
   */
  time: EventCacheInterval;
  searchString?: string;
  searchFilters?: readonly Filter[];
  selectedReportId?: string;
  eventSubtypeFilter?: { [subtype: string]: boolean };
  eventTagsFilter?: string[];
  /** Cameras to fetch events for. */
  cameraIds: readonly { locationId: SiteId; vmsCameraId: VMSCameraId }[];
  /** Include string to send to platform API to fetch tags. */
  include?: string;
  eventFilters?: EventFilterState;
  eqShouldFields?: { [key: string]: any };
  eqMustHaveFields?: { [key: string]: any };
  eqMustNonZeroFields?: readonly string[];
  eqMustNotFields?: { [key: string]: any };
  filters?: readonly Filter[];
  subtitle?: string;

  /** Maximum number of events to return. */
  count?: number;
  sortOrder?: 'asc' | 'desc';
  searchOperator?: SearchOperator;
}

/**
 * Convert a EventClientQueryParameters to a EventCacheQuery.
 *
 * @param eventClientQuery - The query to convert.  If undefined, this will return
 *   a query for all events in the specified time range.
 * @param time - The time to fetch events for.
 * @param cameraIds - The cameras to fetch events for.
 */
export function eventClientToEventCacheQuery(
  eventClientQuery: BasicEventQueryParameters | undefined,
  time: EventCacheInterval,
  cameraIds: readonly { locationId: SiteId; vmsCameraId: VMSCameraId }[],
): EventCacheQuery {
  // Note that we ignore `eventClientQuery.locationId` and `eventClientQuery.locationIds`,
  // as these come from `cameraIds`.

  if (!eventClientQuery) {
    return {
      time,
      cameraIds,
    };
  }
  return {
    time,
    cameraIds,
    searchString: eventClientQuery.searchString,
    searchFilters: eventClientQuery.searchFilters,
    searchOperator: eventClientQuery.searchOperator,
    selectedReportId: eventClientQuery.selectedReportId,
    eventSubtypeFilter: eventClientQuery.eventSubtypeFilter,
    include: eventClientQuery.include,
    eventFilters: eventClientQuery.eventFilterState,
    eqShouldFields: eventClientQuery.eqShouldFields,
    eqMustHaveFields: eventClientQuery.eqMustHaveFields,
    eqMustNonZeroFields: eventClientQuery.eqMustNonZeroFields,
    eqMustNotFields: eventClientQuery.eqMustNotFields,
    filters: eventClientQuery.filters,
    subtitle: eventClientQuery.subtitle,
  };
}

/**
 * Data structure which is stored in the cache.
 */
interface EventCacheData {
  /** Events returned by Platform API. */
  events: IEvent[];
  /**
   * All events from `events`, stored in an interval tree.  When we're looking for
   * a very short timeframe (like all "in progress" events) this allows very efficient
   * querying.
   */
  intervalTree: IntervalTree<IEvent>;
}

/**
 * A CacheBucketDescriptor contains parameters required to fetch the contents of
 * a given cache bucket, either from the cache (via the `key`) or from the
 * PlatformAPI.
 */
interface CacheBucketDescriptor {
  /** The key of this bucket. */
  key: string;
  /** The query parameters used to generate the data in this bucket. */
  params: Omit<EventQueryParameters, 'source'>;
  start: Date;
  end: Date;
}

interface EventFetcher {
  (query: EventQueryParameters): Promise<{
    total: number;
    next?: unknown[];
    count: number;
    results: IEvent[];
  }>;
}

/**
 * EventCache caches events from PlatformApi.  Events are cached on a per-day,
 * per-location basis, so this cache is intended to be used when showing event
 * from a specific location over a specific time period (and ideally from a
 * specific camera).  For example, this is used by the video player to
 * render events in the timeline and to render events near the current event.
 *
 * A given cache bucket holds data from a specific day, from a specific location,
 * and for any given search queries (searchString, searchCapsules, and selectedReportId
 * will all generate different cache buckets, mainly because we don't want to
 * re-implement these searches client-side).
 *
 * This cache currently does not support caching events across locations, so it
 * can't be used in places like the "events" page where we need to show
 * data across multiple locations.  Caching data across locations would be
 * difficult just because of the sheer quantity of events involved.
 *
 * Cached results that are "recent" will use a "stale-while-revalidate" strategy;
 * events from the cache will be returned immediately, but in the background we'll
 * go check with platform API to see if any new events have snuck in.  For older
 * buckets in the cache, we won't bother to revalidate them, because older events
 * are unlikely to change.
 */
export default class EventCache {
  private _cache: WOSCache<EventCacheData, EventCacheQuery, CacheBucketDescriptor, IEvent[]>;

  // This is used to fetch events from the server.  Generally this will just
  // go to PlatformAPI - it's basically here as dependency-injection to make
  // unit tests easier.
  private _fetchEvents: EventFetcher;

  /**
   * The number of times we refreshed a stale cache with data from the server.
   */
  public cacheRefreshes = 0;
  /**
   * The number of times we successfully refreshed the cache by fetching
   * all the new events at the end of "today" without having to re-fetch data.
   */
  public cacheTodayRefreshes = 0;
  /**
   * Create a new EventCache.
   *
   * @param [options.fetcher] - Function to use to fetch events from PlatformApi.
   *   This is mainly exposed for unit testing purposes.
   * @params [options.maxSize] - The maximum number of events to store in the
   *   internal LRU cache.  Defaults to MAX_CACHED_EVENTS.
   */
  constructor(
    options: {
      fetcher?: EventFetcher;
      maxSize?: number;
    } = {},
  ) {
    this._cache = new WOSCache({
      name: 'event cache',
      max: options.maxSize ?? MAX_CACHED_EVENTS,
      length: (entry) => entry.events.length || 1,
      fetchFunction: this._fetchFunction,
      keyFunction: (descriptor: CacheBucketDescriptor) => descriptor.key,
      getRevalidationTimeout: (descriptor: CacheBucketDescriptor) =>
        getRevalidationTimeout(descriptor.start.valueOf()),
      queryMapper: getCacheKeys,
      resultMapper: filterCachedEvents,
    });

    this._fetchEvents =
      options.fetcher || ((query: EventQueryParameters) => PlatformApi().listEvents(query));
  }

  private _fetchFunction = async (
    descriptor: CacheBucketDescriptor,
    oldEntry: EventCacheData | undefined,
  ) => {
    if (!oldEntry) {
      return await this._fetchNewCacheEntry(descriptor);
    }
    return await this._refreshExistingCacheEntry(descriptor, oldEntry);
  };

  /**
   * Returns true if this query can be satisfied from the cache and is not stale.
   */
  isFresh(query: EventCacheQuery) {
    return this._cache.isFresh(query);
  }

  /**
   * Subscribe to events from the cache.
   *
   * This will call the provided `listener`  with data from the cache as soon
   * as some is available, and then call it again whenever data in the cache is
   * updated.
   *
   * @returns a function to call to stop receiving updates for this query.  This
   *   must be called, or we'll query PlatformApi forever!
   *
   * @example
   *     const unsubscribe = eventCache.subscribe((err, events) => console.log(events));
   *     unsubscribe(); // Call when done with the subscription.
   */
  subscribe(
    query: EventCacheQuery,
    listener: (err: Error | undefined, events: IEvent[] | undefined) => void,
  ): () => void {
    return this._cache.subscribe(query, listener);
  }

  /**
   * Retrieves raw `EventCacheData` from the cache.
   *
   * This is a helper function for `getEvents()` and `getEventsSync()`,
   * which returns a `cachedEvents` which is all the events we have
   * in the cache which satisfy the query right now (which may be
   * incomplete or out of date if `stale` is true) and a `cachedEventsPromise`,
   * which resolves once the latest results are available.
   */
  getEvents(query: EventCacheQuery): Promise<IEvent[]> {
    return this._cache.get(query);
  }

  /**
   * Get events from the cache immediately.  If results are not in the cache, this
   * will start workers to fetch them, but events from any missing cache entries
   * will not be returned.  If some cache entries are missing, this will return
   * results from the entries we have.
   */
  getEventsSync(query: EventCacheQuery): { stale: boolean; events: IEvent[] } {
    const { stale, result } = this._cache.getSync(query);
    return { stale, events: result };
  }

  getLastUpdateTime(): number {
    return this._cache.lastUpdate;
  }

  private async _fetchNewCacheEntry(descriptor: CacheBucketDescriptor): Promise<EventCacheData> {
    const events = await fetchEventsFromPlatform(this._fetchEvents, {
      ...descriptor.params,
      source: 'EventCache#_fetchNewCacheEntry',
    });
    return { events, intervalTree: EventUtil.buildIntervalTree(events) };
  }

  /**
   * This will check to see of a given `EventCacheData` is up to date.  If
   * the server has no new events, this will return a new cache entry with the
   * exact same events object and a new fetch time.  If there's new data available,
   * this will fetch the new data.
   */
  private async _refreshExistingCacheEntry(
    descriptor: CacheBucketDescriptor,
    cacheData: EventCacheData,
  ): Promise<EventCacheData> {
    let newResult: EventCacheData;

    // Check with the server to see how many entries it has.  This is much faster
    // than downloading all the events.
    const results = await this._fetchEvents({
      ...descriptor.params,
      count: 1,
      totalLimit: true,
      source: 'EventCache#_refreshExistingCacheEntry',
    });

    const serverEventCount = results.total;

    if (serverEventCount !== cacheData.events.length) {
      // Server has more events than we do.
      this.cacheRefreshes++;

      // Try to fetch "more" events from the server for the existing entry,
      // as this will be faster than re-fetching everything.
      const moreResult = await this._getMoreFromPlatform(descriptor, cacheData, serverEventCount);

      if (moreResult) {
        newResult = moreResult;
      } else {
        // If we couldn't get more results, fetch everything fresh.
        newResult = await this._fetchNewCacheEntry(descriptor);
      }
    } else {
      // Data is up to date.  Return the existing object.
      newResult = cacheData;
    }

    return newResult;
  }

  /**
   * Update an event in the EventCache.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateEvent(event: IEvent) {
    // TODO: Instead of throwing away the whole cache, we could just thow
    // away entries that have the event.
    this._cache.invalidate();
  }

  /**
   * This will try to fetch more events at the end of an existing cache entry
   * (e.g. when we're fetching more data for "today").
   */
  private async _getMoreFromPlatform(
    descriptor: CacheBucketDescriptor,
    existingCacheData: EventCacheData,
    expectedTotal: number,
  ) {
    let newResult: EventCacheData | undefined;

    const existingCacheAge = Date.now() - descriptor.start.valueOf();
    const lastEvent = existingCacheData.events[existingCacheData.events.length - 1];
    if (existingCacheAge < MAX_TODAY_AGE && lastEvent) {
      // Get "new" events, and see if this makes our event total work out.
      // It's much faster to just download a few events from the server than
      // the fetch the whole day.
      const newEvents = await fetchEventsFromPlatform(this._fetchEvents, {
        ...descriptor.params,
        start: new Date(lastEvent.startTime.valueOf() + 1000).toISOString(),
        source: 'EventCache#_getMoreFromPlatform',
      });

      if (existingCacheData.events.length + newEvents.length === expectedTotal) {
        // Success!
        this.cacheTodayRefreshes++;
        const events = existingCacheData.events.concat(newEvents);
        newResult = {
          ...existingCacheData,
          events,
          intervalTree: EventUtil.buildIntervalTree(events),
        };
      }
    }

    return newResult;
  }
}

/**
 * Returns time in milliseconds until this entry should be revalidated, or undefined
 * if the entry does not need to be revalidated.
 */
function getRevalidationTimeout(queryStart: number): number | undefined {
  const eventAge = Date.now() - queryStart;

  for (const rule of REVALIDATION_RULES) {
    if (eventAge < rule.age) {
      return rule.revalidateAfter;
    }
  }

  return undefined;
}

/**
 * Convert an EventCacheQuery into a collection of cache keys and parameters for
 * fetching data to fill the cache.
 */
function getCacheKeys(query: EventCacheQuery) {
  const result: CacheBucketDescriptor[] = [];

  const locationIds = getUniqueLocationIds(query.cameraIds);

  for (const locationId of locationIds) {
    const { params, cacheKey } = prepareCacheQueryParams(locationId, query);

    const startEnd = getQueryStartEnd(query.time);
    let start = startEnd[0];
    const end = startEnd[1];

    while (start.valueOf() < end.valueOf()) {
      const end = moment(start).endOf('day').toDate();
      end.setMilliseconds(0); // Platform API only supports second-precision.
      result.push({
        start,
        end,
        params: {
          ...params,
          count: MAX_EVENTS_SINGLE_FETCH,
          start: start.toISOString(),
          end: end.toISOString(),
        },
        key: `start=${start.valueOf()}&end=${end.valueOf()}&${cacheKey}`,
      });
      start = moment(start).add(1, 'day').toDate();
    }
  }

  return result;
}

/** getQueryStartEnd() returns the start and end times to query for events. */
function getQueryStartEnd(time: EventCacheInterval): [Date, Date] {
  const times = getStartEnd(time);
  const start = isOverlapInterval(time) ? times[0] - MAX_EVENT_LENGTH_FOR_OVERLAP : times[0];
  const end = times[1];

  const startDate = moment(start).startOf('day').toDate();
  const endDate = moment(end).endOf('day').toDate();

  if (isNaN(startDate.valueOf()) || isNaN(endDate.valueOf())) {
    throw new Error(`Invalid query.time: ` + JSON.stringify(time));
  }

  return [startDate, endDate];
}

/** getStartEnd() returns the start and end times from a EventCacheInterval. */
function getStartEnd(time: EventCacheInterval): [number, number] {
  let start: number;
  let end: number;

  if (isOverlapInterval(time)) {
    const overlap = time.overlap ?? MASTER_TIME_RESOLUTION;
    start = time.overlapTime - overlap;
    end = time.overlapTime + overlap;
  } else {
    start = time.start;
    end = time.end;
  }

  return [start, end];
}

/**
 * This will take an EventCacheQuery and convert it into query parameters to send
 * to Platform API.  Note that this will ignore eventFilters and cameraId, since these
 * are filtered out in `filterCachedEvents()`.  Every parameter that's not handled
 * in the filter, though, needs to be part of the query, and also needs to be
 * part of the cache key.
 */
function prepareCacheQueryParams(locationId: string, query: EventCacheQuery) {
  const {
    searchString,
    searchFilters,
    selectedReportId,
    include,
    eqShouldFields,
    filters,
    searchOperator,
    cameraIds,
  } = query;

  const eqMustHaveFields = convertEqMustHaveFields(query.eqMustHaveFields);
  const eqMustNotFields = convertEqMustHaveFields(query.eqMustNotFields);

  const params: Omit<EventQueryParameters, 'start' | 'end' | 'count' | 'source'> = {
    timeFieldToSearchOn: 'startTime',
    sorts: [{ startTime: { order: 'asc' } }],
    sortOrder: 'asc',
    // TODO: This forces Platform API to only return events where the startTime
    // is between start and end - if there are any "long" events that start
    // before the start time, we'll miss them.  Can we take advantage of setting
    // this true to catch long events, instead of using MAX_EVENT_LENGTH_FOR_OVERLAP?
    fullRange: false,
    include,
  };

  const filterKeys: string[] = [];

  //
  // Filtered query params
  //

  if (selectedReportId) {
    filterKeys.push(`reportTemplateId=${selectedReportId}`);
    params.reportTemplateId = selectedReportId;
  }

  if (searchString) {
    filterKeys.push(`searchString=${encodeURIComponent(searchString)}`);
  }

  if (searchFilters) {
    for (const filter of searchFilters) {
      filterKeys.push(`searchFilter=${JSON.stringify(filter)}`);
    }
  }

  // Sort the camera IDs so that we can use them as part of the cache key to get more cache hits.
  const cameraIdList = cameraIds.map((camera) => camera.vmsCameraId).sort();

  const eq = getQueryParameters({
    locationId,
    includeTypes: false,
    eqShouldFields,
    eqMustHaveFields,
    eqMustNotFields,
    searchString,
    filters: [...(filters || []), ...(searchFilters || [])],
    searchOperator,
    cameraIds: cameraIdList,
  });

  if (eqShouldFields) {
    for (const key of Object.keys(eqShouldFields)) {
      filterKeys.push(`eqShouldFields=${key}-${JSON.stringify(eqShouldFields[key])}`);
    }
  }

  if (eqMustHaveFields) {
    for (const key of Object.keys(eqMustHaveFields)) {
      const value = eqMustHaveFields[key];
      filterKeys.push(`eqMustHaveFields=${key}-${JSON.stringify(value)}`);
    }
  }

  if (eqMustNotFields) {
    for (const key of Object.keys(eqMustNotFields)) {
      const value = eqMustNotFields[key];
      filterKeys.push(`eqMustNotFields=${key}-${JSON.stringify(value)}`);
    }
  }

  if (filters) {
    for (const filter of filters) {
      filterKeys.push(`filter=${JSON.stringify(filter)}`);
    }
  }

  if (include) {
    params.include = include;
    filterKeys.push(`include=${include}`);
  }

  Object.assign(params, eq.toFilter().bool);

  const cacheKey = `location=${locationId}&${filterKeys.join('&')}&cameraIds=${cameraIdList.join(
    '&',
  )}`;

  return {
    params,
    cacheKey,
  };
}

/**
 * Fetch events from the platform API.
 */
async function fetchEventsFromPlatform(
  fetchEvents: EventFetcher,
  query: EventQueryParameters,
): Promise<IEvent[]> {
  let events: IEvent[] = [];

  let next: unknown[] | undefined;
  do {
    const results = await fetchEvents({ ...query, next });
    events = events.concat(results.results);
    next = results.next;
  } while (next);

  return events;
}

/**
 * Given a set of events from the cache for a particular location, filter out
 * events that don't match the query.
 *
 * This will only filter based on things that are not part of the cache key
 * (e.g. we'll filter based on event type, since the cache always stores events
 * of all types, but we won't filter based on search since this is already baked
 * into the cache key).
 *
 * `events` should be in sorted order, based on query start time.
 */
function filterCachedEvents(query: EventCacheQuery, events: EventCacheData[]) {
  const {
    count,
    cameraIds,
    time,
    eventFilters,
    sortOrder,
    eventSubtypeFilter,
    eqMustHaveFields,
    eqMustNotFields,
    eqMustNonZeroFields,
  } = query;

  const cameraIdsMap: { [locationId: SiteId]: { [cameraId: VMSCameraId]: boolean } } = {};
  for (const { locationId, vmsCameraId } of cameraIds) {
    cameraIdsMap[locationId] = cameraIdsMap[locationId] || {};
    cameraIdsMap[locationId][vmsCameraId] = true;
  }

  let filteredEvents = events.flatMap((entry) => {
    let events: IEvent[];
    if (isOverlapInterval(time)) {
      const [overlapStart, overlapEnd] = getStartEnd(time);
      events = entry.intervalTree.search(overlapStart, overlapEnd);
    } else {
      events = entry.events;
    }

    return events.filter((event) => {
      // If there's an overlap specified, we already filtered out non-matching events
      // via the interval tree.  Otherwise, use the `start` and `end` to filter events.
      const inRange =
        isOverlapInterval(time) ||
        (event.startTime.valueOf() >= time.start && event.startTime.valueOf() < time.end);

      const isCorrectSubtype = checkEventAgainstSubtypeFilter(event, eventSubtypeFilter);

      const matchesFields =
        checkEqMustHaveFields(event, eqMustHaveFields, false) &&
        checkEqMustHaveFields(event, eqMustNotFields, true) &&
        checkEqMustNonZeroFields(event, eqMustNonZeroFields);

      const eventSiteId = event.siteId;

      const matchingCamera =
        cameraIdsMap[eventSiteId]?.[event.primaryCameraId as VMSCameraId] ||
        !!event.cameras.find((camera) => cameraIdsMap[eventSiteId]?.[camera.id as VMSCameraId]);

      const matchingEventType =
        !eventFilters ||
        (eventFilters.bookmark && event.type === EVENT_TYPE_BOOKMARK) ||
        (eventFilters.motion && event.type === EVENT_TYPE_MOTION) ||
        (eventFilters.transaction && event.type === EVENT_TYPE_TRANSACTION);

      const matchesSaved = !eventFilters || !eventFilters.saved || event.shareStartTime;

      const matchesSubtitle = !query.subtitle || event.subtitle === query.subtitle;

      return (
        inRange &&
        isCorrectSubtype &&
        matchesFields &&
        matchingCamera &&
        matchingEventType &&
        matchesSaved &&
        matchesSubtitle
      );
    });
  });

  const locationIds = getUniqueLocationIds(cameraIds);
  if (locationIds.length <= 1) {
    // If there's only one location, then events will already be in ascending sorted
    // order, because `getCacheKeys()` returns buckets in ascending order.

    if (sortOrder === 'desc') {
      filteredEvents.reverse();
    }
  } else {
    // If there are multiple locations, then events will be in ascending sorted
    // order for each location, but there will be one location right after the next.
    // Need to sort.  TODO: take advantage of the fact that these are already in
    // nearly the correct order to sort them faster.
    filteredEvents = sortBy(filteredEvents, (event) =>
      sortOrder === 'desc' ? -event.startTime : event.startTime,
    );
  }

  if (count !== undefined && count < filteredEvents.length) {
    filteredEvents.length = count;
  }

  return filteredEvents;
}

function getUniqueLocationIds(cameraIds: readonly { locationId: string }[]) {
  return uniq(cameraIds.map(({ locationId }) => locationId));
}

// Returns true if two cache queries are equivalent.
export function cacheQueriesEqual(
  a: EventCacheQuery | undefined,
  b: EventCacheQuery | undefined,
): boolean {
  return isEqualWith(a, b, (a, b, key) => {
    switch (key) {
      case 'cameraIds':
        // These are immutable - compare directly.
        return a === b;
      case 'searchCapsules':
        return a === b;
      case 'eventFilters':
        return (isDefaultEventFilters(a) && isDefaultEventFilters(b)) || isEqual(a, b);
      default:
        // All other filters are basic types, except for "time" which we want to deep compare.
        return undefined;
    }
  });
}
